import React from 'react';

import { addSeconds } from 'date-fns';

import DeliveryDetails from 'components/order-status/components/delivery-details';
import { StyledIcon, StyledText } from 'components/order-status/styled';
import { IOrderInProgress, IOrderInProgressResult } from 'components/order-status/types';
import { isOrderTimeInInterval } from 'state/order/utils';
import { ServiceMode } from 'state/service-mode';
import { theme } from 'styles/configure-theme';

export const orderInProgress = ({
  recentOrder,
  fireOrderAhead,
  isDeliveryOrderCompleted,
  isPreConfirmCurbside,
  isCurbside,
  reversedUI,
  formatMessage,
}: IOrderInProgress): IOrderInProgressResult | null => {
  const iconColor = reversedUI ? theme.token('icon-reversed') : theme.token('icon-default');
  const PICKUP_UPDATED_TIMEOUT_IN_MINUTES = 10;
  if (!recentOrder) {
    return null;
  }

  const isPickupTimedOut = () => {
    // Add the fire order delay to the total timeout
    const fireOrderInSeconds = recentOrder?.fireOrderIn
      ? recentOrder?.fireOrderIn + fireOrderAhead
      : 0;
    const fireOrderInMinutes = fireOrderInSeconds / 60;
    const timeOutWithDelay = PICKUP_UPDATED_TIMEOUT_IN_MINUTES + fireOrderInMinutes;

    return (
      recentOrder &&
      !recentOrder?.delivery &&
      isOrderTimeInInterval(recentOrder?.updatedAt, timeOutWithDelay)
    );
  };

  /**
   * Defines the pickup time based on the order created date, fireOrderIn and fireOrderAhead values
   * fireOrderIn - the number of seconds after the order is placed when it should be released to kitchen
   * fireOrderAhead - the number of seconds before pickup time when the order is released to kitchen (configured with LD flag per brand)
   */
  let pickupDate = new Date();
  if (recentOrder?.createdAt) {
    pickupDate = new Date(recentOrder?.createdAt);

    if (recentOrder?.fireOrderIn) {
      pickupDate = addSeconds(pickupDate, (recentOrder.fireOrderIn || 0) + (fireOrderAhead ?? 0));
    }
  }

  const formattedOrderTime = pickupDate
    .toLocaleString('en-us', {
      hour: 'numeric',
      minute: 'numeric',
    })
    .toLowerCase()
    .replace(' ', '');

  const orderPlacedText = (
    <StyledText>
      {formatMessage({ id: 'orderPlaced' })}: <StyledText>{formattedOrderTime}</StyledText>
    </StyledText>
  );

  // CURBSIDE - Before order is commited on confirming arrival , selecting "I'm here!"
  if (isCurbside && isPreConfirmCurbside) {
    return {
      details: <StyledText>{formatMessage({ id: 'letUsKnowWhenYourAreHere' })}</StyledText>,
      icon: <StyledIcon variant="driveThru" color={iconColor} />,
    };
  }
  // CURBSIDE - After order is placed
  if (isCurbside && isPickupTimedOut()) {
    return {
      details: orderPlacedText,
      icon: <StyledIcon variant="driveThru" color={iconColor} />,
    };
  }
  // DRIVE_THRU
  if (recentOrder && ServiceMode.DRIVE_THRU === recentOrder?.cart?.serviceMode) {
    return {
      details: orderPlacedText,
      icon: <StyledIcon variant="driveThru" color={iconColor} />,
    };
  }
  if (recentOrder && ServiceMode.EAT_IN === recentOrder?.cart?.serviceMode) {
    return {
      details: recentOrder?.fireOrderIn ? (
        <StyledText>
          {formatMessage({ id: 'scheduledTime' })} {formattedOrderTime}
        </StyledText>
      ) : (
        orderPlacedText
      ),
      icon: <StyledIcon variant="restaurant" color={iconColor} />,
    };
  }
  // PICKUP
  if (recentOrder && isPickupTimedOut()) {
    return {
      details: recentOrder?.fireOrderIn ? (
        <StyledText>
          {formatMessage({ id: 'scheduledTime' })} {formattedOrderTime}
        </StyledText>
      ) : (
        orderPlacedText
      ),
      icon: <StyledIcon variant="store" color={iconColor} />,
    };
  }
  // DELIVERY
  if (recentOrder?.delivery && !isDeliveryOrderCompleted) {
    return {
      details: <DeliveryDetails serverOrder={recentOrder} />,
      icon: <StyledIcon variant="delivery" color={iconColor} />,
    };
  }

  return null;
};

export const getServiceModeHeaderText = (formatMessage: Function, serviceMode?: ServiceMode) => {
  switch (serviceMode) {
    case ServiceMode.DELIVERY:
      return formatMessage({ id: 'delivery' });
    case ServiceMode.EAT_IN:
      return formatMessage({ id: 'dineIn' });
    case ServiceMode.DRIVE_THRU:
      return formatMessage({ id: 'driveThru' });
    case ServiceMode.CATERING_PICKUP:
      return formatMessage({ id: 'catering' });
    case ServiceMode.CATERING_DELIVERY:
      return formatMessage({ id: 'catering' });
    case ServiceMode.CURBSIDE:
      return formatMessage({ id: 'curbside' });
    case ServiceMode.TAKEOUT:
      return formatMessage({ id: 'pickUp' });
  }
};
